// Push notification service — permission, FCM token lifecycle, backend
// registration and the foreground message stream.
//
// Transport lives in lib/api (pushTokensApi); this module owns only the FCM
// side and the local bookkeeping that keeps registration idempotent. UI
// components call these functions and never touch firebase/messaging directly
// (CLAUDE.md: business logic outside UI).
//
// Registration is deliberately chatty-free: the token that was last accepted by
// the API is remembered per user, so remounts, Strict Mode's double effect and
// repeat logins do not re-POST an unchanged token. FCM rotation changes the
// token value, which invalidates that record and triggers one fresh POST.

import { deleteToken, getToken, onMessage, type MessagePayload } from "firebase/messaging";
import { ApiError, pushTokensApi } from "./api";
import { firebaseMessaging, isFirebaseConfigured, vapidKey } from "./firebase";

/** Where the messaging service worker is served from. Must stay in sync with
 *  public/firebase-messaging-sw.js — FCM registers this scope itself. */
const SW_PATH = "/firebase-messaging-sw.js";

/** Records the token the API last accepted, keyed by user, so a reload does not
 *  re-register. Session-scoped on purpose: a fresh browser session re-asserts
 *  the binding, which self-heals tokens the server pruned as stale. */
const REGISTERED_KEY = "bell.push.registered";

export type PermissionState = "granted" | "denied" | "default" | "unsupported";

/** Why a token could not be produced. Callers surface these to the user. */
export type TokenFailure =
  | "unsupported"
  | "unconfigured"
  | "permission-denied"
  | "sw-failed"
  | "token-failed";

export interface TokenResult {
  token: string | null;
  reason?: TokenFailure;
}

// ---------------------------------------------------------------------------
// Capability + permission
// ---------------------------------------------------------------------------

/** True when this browser exposes everything FCM web push needs. Also false
 *  during SSR. Note this is the synchronous, cheap check — firebaseMessaging()
 *  runs Firebase's own async isSupported() on top. */
export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

/** Current permission without prompting. */
export function permissionState(): PermissionState {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission as PermissionState;
}

/** Requests permission, prompting only when the state is still `default`.
 *
 *  Browsers permanently reject repeat prompts after a denial (and Chrome
 *  penalizes origins that ask reflexively), so an already-decided state short-
 *  circuits: callers get the standing answer without a second dialog. */
export async function requestNotificationPermission(): Promise<PermissionState> {
  if (!isPushSupported()) return "unsupported";

  const current = Notification.permission as PermissionState;
  if (current !== "default") {
    return current;
  }

  try {
    return (await Notification.requestPermission()) as PermissionState;
  } catch {
    // Safari < 16 only supports the callback form and throws on the promise one.
    return Notification.permission as PermissionState;
  }
}

// ---------------------------------------------------------------------------
// Service worker
// ---------------------------------------------------------------------------

let swRegistration: Promise<ServiceWorkerRegistration | null> | null = null;

/** Registers the messaging service worker, memoized per page load.
 *
 *  Registering it explicitly (rather than letting getToken() do it) keeps the
 *  failure separable: a 404 or MIME error on the SW file reports as
 *  `sw-failed`, not as a generic token error. */
export function registerMessagingServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return Promise.resolve(null);
  }

  if (swRegistration) return swRegistration;

  const pending = navigator.serviceWorker
    // updateViaCache: "none" so a redeployed SW is fetched, not served from
    // the HTTP cache — otherwise clients keep an old handler for days.
    .register(SW_PATH, { scope: "/", updateViaCache: "none" })
    .then(async (registration) => {
      // register() resolves as soon as the browser accepts the registration —
      // not once the worker is active. On a fresh install (first visit in a
      // browser) the worker is still "installing"/"activating" at this point,
      // and PushManager.subscribe() throws AbortError ("no active Service
      // Worker") if called before activation finishes. `ready` resolves only
      // once this scope has an active worker, so waiting here removes the race.
      if (!registration.active) {
        await navigator.serviceWorker.ready;
      }
      return registration;
    })
    .catch((err: unknown) => {
      // Only successes stay memoized. Caching a transient failure (offline at
      // page load) would make every later "Enable notifications" click fail for
      // the life of the page. Callers awaiting `pending` still get null.
      if (swRegistration === pending) swRegistration = null;
      void err;
      return null;
    });

  swRegistration = pending;
  return pending;
}

// ---------------------------------------------------------------------------
// Token
// ---------------------------------------------------------------------------

/** Mints (or returns the cached) FCM registration token.
 *
 *  Never prompts: permission must already be granted, which keeps the browser
 *  dialog tied to an explicit user action in the UI layer. */
export async function getOrRefreshFcmToken(): Promise<TokenResult> {
  if (!isPushSupported()) {
    return { token: null, reason: "unsupported" };
  }

  if (!isFirebaseConfigured()) {
    return { token: null, reason: "unconfigured" };
  }

  if (Notification.permission !== "granted") {
    return { token: null, reason: "permission-denied" };
  }

  const messaging = await firebaseMessaging();
  if (!messaging) {
    return { token: null, reason: "unsupported" };
  }

  const registration = await registerMessagingServiceWorker();
  if (!registration) {
    return { token: null, reason: "sw-failed" };
  }

  try {
    const token = await getToken(messaging, {
      vapidKey: vapidKey() ?? undefined,
      serviceWorkerRegistration: registration,
    });
    if (!token) {
      return { token: null, reason: "token-failed" };
    }
    return { token };
  } catch {
    // Almost always a VAPID key that does not belong to this project, or the
    // Cloud Messaging API (V1) still disabled in the Firebase console. Nothing
    // the caller can act on beyond the `token-failed` reason.
    return { token: null, reason: "token-failed" };
  }
}

/** Drops the token from FCM so this browser stops being a delivery target.
 *  Best-effort: failure is not worth blocking a sign-out. */
export async function deleteFcmToken(): Promise<void> {
  try {
    const messaging = await firebaseMessaging();
    if (messaging) {
      await deleteToken(messaging);
    }
  } catch {
    // Ignored — the backend row is already gone by this point.
  }
}

// ---------------------------------------------------------------------------
// Backend registration
// ---------------------------------------------------------------------------

function readRegistered(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(REGISTERED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function writeRegistered(map: Record<string, string>): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(REGISTERED_KEY, JSON.stringify(map));
  } catch {
    // Storage full / disabled: the only cost is a redundant POST next reload.
  }
}

/** True when `token` is already registered for `userId` in this session. */
export function isTokenRegistered(token: string, userId: string): boolean {
  return readRegistered()[userId] === token;
}

/** Forgets the local registration record for a user (or all users). Called on
 *  logout so the next sign-in re-asserts the binding. */
export function clearRegistrationRecord(userId?: string): void {
  if (!userId) {
    if (typeof window !== "undefined") window.sessionStorage.removeItem(REGISTERED_KEY);
    return;
  }
  const map = readRegistered();
  delete map[userId];
  writeRegistered(map);
}

/** POSTs the token unless this session already registered it for this user.
 *  Returns true when the API holds the binding (freshly sent or already known). */
export async function registerTokenWithBackend(
  token: string,
  userId: string,
): Promise<boolean> {
  if (isTokenRegistered(token, userId)) {
    return true;
  }

  try {
    await pushTokensApi.register(token, "WEB");
    writeRegistered({ ...readRegistered(), [userId]: token });
    return true;
  } catch (err) {
    // A 401 means the JWT died; lib/api already cleared the session and the
    // route guard is redirecting. Nothing to retry against.
    if (err instanceof ApiError && err.status === 401) {
      return false;
    }
    return false;
  }
}

/** DELETEs the token. Resolves false on failure — callers must not let this
 *  block sign-out. */
export async function unregisterTokenFromBackend(token: string): Promise<boolean> {
  try {
    await pushTokensApi.unregister(token);
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Foreground messages
// ---------------------------------------------------------------------------

/** Subscribes to messages that arrive while a dashboard tab is focused.
 *
 *  FCM routes to exactly one place: onMessage fires for foreground tabs and the
 *  service worker handles the rest, so an in-app toast can never double up with
 *  a system notification for the same message.
 *
 *  Returns an unsubscribe function; safe to call even if the subscription never
 *  materialized (unsupported browser, missing config). */
export function listenForForegroundMessages(
  callback: (payload: MessagePayload) => void,
): () => void {
  let unsubscribe: (() => void) | null = null;
  let cancelled = false;

  void (async () => {
    const messaging = await firebaseMessaging();
    // The caller may have unmounted while messaging was resolving.
    if (!messaging) {
      return;
    }
    if (cancelled) {
      return;
    }
    try {
      unsubscribe = onMessage(messaging, (payload) => {
        callback(payload);
      });
    } catch {
      // Non-fatal: the tab simply gets no live toasts.
    }
  })();

  return () => {
    cancelled = true;
    unsubscribe?.();
    unsubscribe = null;
  };
}

export type { MessagePayload };
